import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { CustomerForm } from "@/components/customers/CustomerForm";
import ManagerPinModal from "@/components/ManagerPinModal";

// Customer Email Accuracy & Protection — a customer-facing document may ONLY ever be
// emailed to the address saved on the linked customer's account; nobody can type an
// arbitrary recipient into a send dialog. This is the single shared gate every
// document-email dialog (DocumentShareDialog, QuotePreviewDialog) renders in place of
// a free-text "Email to" field: either `email` is present and `children` (the real
// Send button, owned by the caller) renders, or one of two guided blocks does —
// (a) no customer linked at all, or (b) a customer is linked but has no email yet,
// with an "Edit Customer" action that reuses the existing CustomerForm in place so the
// caller's own dialog/work is never lost. The backend enforces this identically
// (ignores any client-supplied `to`, resolves strictly from the customer record) —
// this component is the matching UI, not the only guard.
export default function CustomerEmailGate({ customerId, email, onEmailResolved, onCustomerUpdated, children }) {
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [business, setBusiness] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinMsg, setPinMsg] = useState("");

  const openEdit = async () => {
    try {
      const [{ data: cust }, { data: biz }] = await Promise.all([api.get(`/customers/${customerId}`), api.get("/business")]);
      setEditForm(cust);
      setBusiness(biz);
      setEditOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not load the customer.");
    }
  };

  const save = async (pin = null) => {
    const body = { ...editForm };
    if (pin) body.manager_pin = pin;
    try {
      const { data } = await api.put(`/customers/${customerId}`, body);
      toast.success("Customer updated — recipient refreshed.");
      setEditOpen(false); setPinOpen(false);
      onEmailResolved?.(data.email || "");
      onCustomerUpdated?.(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) { setPinMsg(d.message); setPinOpen(true); throw new Error(d.message); }
      toast.error(apiError(d));
      throw e;
    }
  };

  if (!customerId) {
    return (
      <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="no-customer-linked-notice">
        <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> No customer linked</p>
        <p className="text-xs text-muted-foreground mt-1">This document has no customer account linked, so there's no saved address to email it to. Select or create a customer first — Share Document and Download PDF are still available.</p>
      </div>
    );
  }

  if (!email) {
    return (
      <>
        <div className="rounded-lg border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="no-customer-email-notice">
          <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> No customer email on file</p>
          <p className="text-xs text-muted-foreground mt-1 mb-2">Add an email in the customer's account before sending.</p>
          <Button size="sm" variant="outline" onClick={openEdit} data-testid="edit-customer-for-email-btn">Edit Customer</Button>
        </div>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gate-edit-customer-form">
            <DialogHeader><DialogTitle className="font-head">Edit Customer</DialogTitle></DialogHeader>
            {editForm && business && <CustomerForm form={editForm} setForm={setEditForm} business={business} mode="edit" />}
            <Button onClick={() => save()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gate-save-customer-btn">Save &amp; Use This Email</Button>
          </DialogContent>
        </Dialog>
        <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)} title="Approval Required" reason={pinMsg} onSubmit={(pin) => save(pin)} />
      </>
    );
  }

  return children;
}
